import { useQuery, useMutation, UseQueryOptions, UseMutationOptions } from '@tanstack/react-query'
import { useAuth } from '@clerk/clerk-react'
import { useAxios } from '../contexts/AxiosProvider'

type RequestConfig = {
  url: string
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE'
  data?: any
  params?: Record<string, any>
}

export function useAxiosQuery<TData = any>(
  config: Omit<RequestConfig, 'data'>,
  options?: UseQueryOptions<TData>,
) {
  const { url, method, params } = config
  const { isLoaded } = useAuth()
  const axios = useAxios()

  return useQuery<TData>({
    queryKey: [url, params],
    queryFn: async () => {
      const response = await axios({
        url,
        method: method ?? 'GET',
        params,
      })
      if (response == null || response.data == null) {
        throw new Error('Empty response from server')
      }
      return response.data
    },
    enabled: isLoaded && Boolean(url), // Only run query when Clerk is loaded
    retry: 3,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000),
    ...options,
  })
}

export function useAxiosMutation<TData = any, TVariables = any>(
  config: Omit<RequestConfig, 'params'>,
  options?: Omit<UseMutationOptions<TData, unknown, TVariables>, 'mutationFn'>,
) {
  const { url, method } = config
  const axios = useAxios()

  return useMutation<TData, unknown, TVariables>({
    mutationFn: async (variables: TVariables) => {
      const response = await axios({
        url,
        method: method ?? 'POST',
        data: variables,
      })
      return response.data
    },
    ...options,
  })
}
